import type {
  ConversationId,
  ConversationThreadId,
  ShopperAiIntegrationId,
} from "@redotech/effect-api-shared/ids/definitions";
import type {
  CreateDraftConversationInput,
  PublishDraftConversationInput,
  StartConversationInput,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import { useRequiredContext } from "@redotech/react-util/context";
import { EffectApiClientContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { conversationQueryKeys } from "../db/collections";
import { catchApiErrors } from "../db/error-handling";

export function useStartConversation() {
  const effectApi = useRequiredContext(EffectApiClientContext);

  const mutation = useMutation({
    mutationFn: async (input: StartConversationInput) => {
      const client = effectApi.getClient();
      return await effectApi.runPromise(
        catchApiErrors(
          client.conversation.startConversation({ payload: input }),
        ),
      );
    },
    onSuccess: () => {},
  });

  return {
    startConversation: mutation.mutateAsync,
    isStarting: mutation.isPending,
  };
}

export function useCreateDraftConversation() {
  const effectApi = useRequiredContext(EffectApiClientContext);

  const mutation = useMutation({
    mutationFn: async (input: CreateDraftConversationInput) => {
      const client = effectApi.getClient();
      return await effectApi.runPromise(
        catchApiErrors(
          client.conversation.createDraftConversation({ payload: input }),
        ),
      );
    },
    onSuccess: () => {},
  });

  return {
    createDraftConversation: mutation.mutateAsync,
    isCreatingDraft: mutation.isPending,
  };
}

export function useSaveConversationDraft() {
  const effectApi = useRequiredContext(EffectApiClientContext);

  const mutation = useMutation({
    mutationFn: async ({
      conversationId,
      threadId,
      content,
      cc,
      bcc,
    }: {
      conversationId: ConversationId;
      threadId: ConversationThreadId;
      content: string;
      cc?: Array<{ email: string; name: string | null }>;
      bcc?: Array<{ email: string; name: string | null }>;
    }) => {
      const client = effectApi.getClient();
      return await effectApi.runPromise(
        catchApiErrors(
          client.conversation.saveDraft({
            path: { id: conversationId },
            payload: { threadId, content, attachments: [], cc, bcc },
          }),
        ),
      );
    },
    onSuccess: (_data, variables) => {},
  });

  return {
    saveConversationDraft: mutation.mutateAsync,
    isSavingDraft: mutation.isPending,
  };
}

export function usePublishDraftConversation() {
  const effectApi = useRequiredContext(EffectApiClientContext);

  const mutation = useMutation({
    mutationFn: async ({
      conversationId,
      ...input
    }: PublishDraftConversationInput & { conversationId: ConversationId }) => {
      const client = effectApi.getClient();
      return await effectApi.runPromise(
        catchApiErrors(
          client.conversation.publishDraftConversation({
            path: { id: conversationId },
            payload: input,
          }),
        ),
      );
    },
    onSuccess: () => {},
  });

  return {
    publishDraftConversation: mutation.mutateAsync,
    isPublishing: mutation.isPending,
  };
}

interface CreateAndPublishInput {
  integrationType: "gmail" | "outlook";
  integrationId: ShopperAiIntegrationId;
  existingConversationId?: ConversationId;
  primaryEmail: string;
  subject: string;
  content: string;
  to: Array<{ email: string; name: string | null }>;
  cc?: Array<{ email: string; name: string | null }>;
  bcc?: Array<{ email: string; name: string | null }>;
  onCreated: (conversationId: string) => void;
}

export function useCreateAndPublishConversation() {
  const effectApi = useRequiredContext(EffectApiClientContext);
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const createAndPublish = async ({
    integrationType,
    integrationId,
    existingConversationId,
    primaryEmail,
    subject,
    content,
    to,
    cc,
    bcc,
    onCreated,
  }: CreateAndPublishInput) => {
    setIsSubmitting(true);
    try {
      const client = effectApi.getClient();

      let conversationId: ConversationId;
      if (existingConversationId) {
        conversationId = existingConversationId;
      } else {
        const draftResult = await effectApi.runPromise(
          catchApiErrors(
            client.conversation.createDraftConversation({
              payload: {
                integrationId,
                customerEmail: primaryEmail || undefined,
                subject: subject || undefined,
              },
            }),
          ),
        );
        conversationId = draftResult.conversationId;
      }

      await effectApi.runPromise(
        catchApiErrors(
          client.conversation.publishDraftConversation({
            path: { id: conversationId },
            payload: {
              customerEmail: primaryEmail,
              subject,
              content,
              attachments: [],
              to,
              cc,
              bcc,
            },
          }),
        ),
      );

      onCreated(conversationId);
      void queryClient.invalidateQueries({
        queryKey: conversationQueryKeys.all,
      });
    } catch (error) {
      console.error("Failed to create and publish conversation", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return { createAndPublish, isSubmitting };
}
